import React, { useEffect, useState } from 'react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
    Config,
    Colours,
    Sizes,
    Caption,
    formatPhoneNumber,
    PrimaryAnchor,
    AlternateAnchor,
    MessagePropType,
    messageToString,
    scrollToTop,
    IconCheckmark,
    AnimatedFadeContainer,
    useIsMounted,
} from '@ratehub/base-ui';

import { COMMON_BUTTON_MESSAGES } from '../definitions/CommonMessages';
import ProviderPropType from '../definitions/ProviderPropType';
import BackButton from './BackButton';
import BrokerHoursBlock from './BrokerHoursBlock';
import ConfirmationForm from './ConfirmationForm';


const RH_INSURANCE_ID = 'rh-insurance';

function ConfirmationPageTemplate({
    title,
    subtitle,
    bookingURL,
    quoteId,
    onBackClick,
    customCTA,
    hideContactInformation = false,
    hideDisclaimer = false,
    hideScheduleCallButton = false,

    values,
    showForm = false,
    onSubmit,

    provider,
    details,
    ...otherProps
}) {
    // We want to make sure we are starting at the top of the Confirmation page
    useEffect(() => {
        scrollToTop(false);
    }, []);

    const intl = useIntl();
    const isMounted = useIsMounted();
    const [ shouldShowForm, setShouldShowForm ] = useState(showForm);
    const [ shouldShowSuccessMessage, setShouldShowSuccessMessage ] = useState(false);

    useEffect(() => {
        if (shouldShowSuccessMessage) {
            setTimeout(
                () => isMounted() && setShouldShowSuccessMessage(false),
                4000,
            );
        }
    }, [ shouldShowSuccessMessage ]);

    // Surex quotes start with an underscore, we want to display the quote without it
    //  so its easier for the user to understand
    const updatedQuoteId = quoteId && provider.id === 'surex-direct'
        ? quoteId.substring(1)
        : quoteId;

    const isBrokeredByRH = provider.id.includes(RH_INSURANCE_ID);
    const primaryAnchorIsCustom = customCTA && !provider.phoneNumber;
    
    function handleSubmit(formValues) {
        onSubmit(formValues);
        setShouldShowForm(false);
        setShouldShowSuccessMessage(true);
    }

    return (
        <StyledComponent {...otherProps}>
            <section className="quote-confirmation-section rh-bg-stone-lightest">
                <If condition={shouldShowForm}>
                    <ConfirmationForm
                        firstName={values.firstName}
                        lastName={values.lastName}
                        phone={values.phone}
                        email={values.email}

                        onSubmit={handleSubmit}
                    />
                </If>
                
                <AnimatedFadeContainer
                    isVisible={shouldShowSuccessMessage}
                    withColour={false}
                >
                    <IconCheckmark
                        className="rh-icon-xl rh-display-block rh-mx-auto"
                        outlineWidth="0"
                        strokeWidth="4px"
                        stroke={Colours.LIME}
                        fill={Colours.LIME_LIGHT}
                    />
                    <h1 className="rh-title-m rh-text-align-center">
                        <FormattedMessage
                            id="InsCommon.ConfirmationPage.Success"
                            defaultMessage="Success! Your details are confirmed"
                        />
                    </h1>
                </AnimatedFadeContainer>

                <If condition={!shouldShowForm && !shouldShowSuccessMessage}>
                    <h1 className="rh-title-m rh-m-0">
                        {
                            // If no title supplied, use a default title
                            title
                            ?? (
                                <FormattedMessage
                                    id="InsCommon.ConfirmationPage.QuoteTitle"
                                    defaultMessage="Quote confirmation"
                                />
                            )
                        }
                    </h1>

                    <If condition={subtitle}>
                        <h2 className="rh-text-m weight-regular rh-mb-2">
                            {messageToString(subtitle, intl)}
                        </h2>
                    </If>

                    <If condition={quoteId || provider.phoneNumber}>
                        <div className="quote-info-container rh-b-1px-solid-stone rh-bg-coconut">
                            <If condition={quoteId}>
                                <div className="quote-info rh-px-1_5 rh-py-2">
                                    <p className="rh-text-s rh-m-0">
                                        <FormattedMessage
                                            id="InsCommon.ConfirmationPage.QuoteIdHeading"
                                            defaultMessage="Reference your Quote ID"
                                        />
                                    </p>
                                    <h3 className="quote-id rh-title-m rh-m-0">
                                        {updatedQuoteId}
                                    </h3>
                                </div>
                            </If>

                            <If condition={provider.phoneNumber}>
                                <div className="quote-info rh-px-1_5 rh-py-2">
                                    <p className="rh-text-s rh-m-0">
                                        {provider.name}
                                    </p>
                                    <a
                                        href={`tel:${provider.phoneNumber}`}
                                        className="phone rh-title-m rh-m-0"
                                    >
                                        {formatPhoneNumber(provider.phoneNumber)}
                                    </a>
                                </div>
                            </If>
                        </div>
                    </If>

                    <div className="rh-display-flex rh-flex-wrap rh-justify-content-space-between rh-gap-1 rh-my-2">
                        <Choose>
                            <When condition={
                                isBrokeredByRH // Cleanup in INS-3594
                                && !hideScheduleCallButton}
                            >
                                <PrimaryAnchor
                                    className="call-schedule"
                                    href={bookingURL ?? Config.RH_INSURANCE_BASE_ACUITY_BOOKING_URL}
                                    message={COMMON_BUTTON_MESSAGES.SCHEDULE_CALL}
                                    data-name="ProviderContactBlock-scheduleCall"
                                />
                            </When>

                            <When condition={primaryAnchorIsCustom}>
                                <PrimaryAnchor
                                    className="call-schedule"
                                    href={customCTA.href}
                                    message={customCTA.message}
                                    data-name={`ProviderContactBlock-${customCTA.dataName}`}
                                />
                            </When>

                            <Otherwise>
                                <PrimaryAnchor
                                    className="call-schedule"
                                    href={`tel:${provider.phoneNumber}`}
                                    message={COMMON_BUTTON_MESSAGES.CALL_NOW}
                                    data-name="ProviderContactBlock-callNow"
                                />
                            </Otherwise>
                        </Choose>

                        <Choose>
                            <When condition={
                                isBrokeredByRH // Cleanup in INS-3594
                                && !hideScheduleCallButton}
                            >
                                <AlternateAnchor
                                    className="call-schedule"
                                    href={`tel:${provider.phoneNumber}`}
                                    message={COMMON_BUTTON_MESSAGES.CALL_NOW}
                                    data-name="ProviderContactBlock-callNow"
                                    variant="blueberry-dark"
                                />
                            </When>

                            <When condition={customCTA && !primaryAnchorIsCustom}>
                                <AlternateAnchor
                                    className="call-schedule"
                                    href={customCTA.href}
                                    message={customCTA.message}
                                    data-name={`ProviderContactBlock-${customCTA.dataName}`}
                                    variant="blueberry-dark"
                                />
                            </When>
                        </Choose>
                    </div>

                    <If condition={onBackClick}>
                        <BackButton
                            onClick={onBackClick}
                            data-name="ConfirmationPage-back"
                            className="rh-mt-4"
                        >
                            <FormattedMessage
                                id="InsCommon.ConfirmationPage.Back"
                                defaultMessage="Back to results"
                            />
                        </BackButton>
                    </If>
                </If>
            </section>

            <section className="quote-details rh-display-flex rh-justify-content-space-between">
                <div className="selected-quote-summary rh-display-flex rh-flex-wrap rh-justify-content-center">
                    <h3 className="rh-m-0">
                        <FormattedMessage
                            id="InsCommon.ConfirmationPage.SummaryTitle"
                            defaultMessage="Your quote details"
                        />
                    </h3>
                    <div className="brokered-by-block rh-display-flex rh-flex-wrap rh-justify-content-center rh-align-items-center">
                        <span className="rh-text-s">
                            <FormattedMessage
                                id="InsCommon.ConfirmationPage.BrokeredBy"
                                defaultMessage="Brokered by:"
                            />
                        </span>
                        <img
                            className="provider-logo rh-my-0_5 rh-mx-1"
                            src={provider.logoPath}
                            alt={provider.name}
                        />
                    </div>
                    <For
                        each="summaryBlock"
                        index="index"
                        of={details}
                    >
                        <div
                            key={index}
                            className="quote-summary-block rh-my-1"
                        >
                            <h2 className="quote-summary-block-heading rh-text-m rh-mb-0_5 weight-medium">
                                {messageToString(summaryBlock.title, intl)}
                            </h2>
                            <div>
                                <For
                                    each="summaryItem"
                                    index="itemIndex"
                                    of={summaryBlock.items}
                                >
                                    <div
                                        key={itemIndex}
                                        className="rh-display-flex rh-flex-nowrap rh-align-items-flex-start"
                                    >
                                        <span className="summary-item-label rh-text-s rh-my-0_125">
                                            {messageToString(summaryItem.label, intl)}
                                        </span>
                                        <span className="summary-item-value rh-text-s weight-medium rh-mx-0_5 rh-my-0_125">
                                            {summaryItem.isDate ? summaryItem.value : messageToString(summaryItem.value, intl)}
                                        </span>
                                    </div>
                                </For>
                            </div>
                        </div>
                    </For>

                    <If condition={!hideContactInformation}>
                        <div className="quote-summary-block rh-my-1">
                            <h2 className="quote-summary-block-heading rh-text-m rh-mb-0_5 weight-medium">
                                <FormattedMessage
                                    id="InsCommon.ConfirmationPage.ContactInfo"
                                    defaultMessage="Contact information"
                                />
                            </h2>
                            <div className="rh-display-flex rh-flex-nowrap rh-align-items-flex-start">
                                <span className="summary-item-label rh-text-s rh-my-0_125">
                                    <FormattedMessage
                                        id="InsCommon.ConfirmationPage.HoursOfOperation"
                                        defaultMessage="Office hours"
                                    />
                                </span>
                                <BrokerHoursBlock
                                    hours={toJS(provider.hours)}
                                    className="summary-item-value rh-mx-0_5 rh-my-0_125"
                                />
                            </div>
                        </div>
                    </If>
                </div>
                <If condition={!hideDisclaimer}>
                    <Caption
                        className="disclaimer rh-pt-0_75"
                        message={(
                            <FormattedMessage
                                id="InsCommon.ConfirmationPage.Disclaimer"
                                defaultMessage="All quoted rates subject to change based on the insurance provider’s discretion. A quote is an estimate based on the information you’ve provided. Please discuss your quoted rate options further with your agent or broker."
                            />
                        )}
                    />
                </If>
            </section>
        </StyledComponent>
    );
}

ConfirmationPageTemplate.propTypes = {
    title: MessagePropType,
    subtitle: MessagePropType,

    values: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        phone: PropTypes.string,
        email: PropTypes.string,
    }),
    showForm: PropTypes.bool,
    onSubmit: PropTypes.func,

    quoteId: PropTypes.string,
    bookingURL: PropTypes.string,
    provider: ProviderPropType,
    details: PropTypes.arrayOf(PropTypes.shape({
        title: MessagePropType.isRequired,
        items: PropTypes.arrayOf(PropTypes.shape({
            label: MessagePropType.isRequired,
            value: MessagePropType,
            isDate: PropTypes.bool, // Required true if passing <FormattedDate>
        })).isRequired,
    })).isRequired,

    onBackClick: PropTypes.func,

    customCTA: PropTypes.shape({
        href: PropTypes.string.isRequired,
        message: MessagePropType.isRequired,
        dataName: PropTypes.string.isRequired,
    }),

    hideContactInformation: PropTypes.bool,
    hideDisclaimer: PropTypes.bool,
    hideScheduleCallButton: PropTypes.bool,
};

const StyledComponent = styled.div`
    /* Need the top margin so that the page content
    does not cover the RH header drop shadow
    The value is really small so it will hardly be noticed */
    margin-top: 4px;

    /* Flex for IE11 */
    display: flex;
    flex-wrap: wrap;

    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    grid-template-rows: 1fr;

    min-height: 100vh;

    > .quote-confirmation-section {
        padding: 5% 10%;
        flex-basis: 50%;

        > .quote-info-container {
            border-radius: ${Sizes.SPACING.HALF};

            > .quote-info {
                /* For instances where the quoteId is excluded */
                &:not(:last-child) {
                    border-bottom: 1px solid ${Colours.STONE};
                }

                > .quote-id {
                    word-break: break-word;
                }

                > .phone {
                    text-decoration: none;
                    color: ${Colours.BLUEBERRY};
                }
            }
        }

        .call-schedule {
            flex: 1 0 200px;
        }
    }

    > .quote-details {
        padding: 5% 10%;

        flex-basis: 50%;
        flex-flow: column;

        max-height: 85vh;

        > .selected-quote-summary {
            height: fit-content;
            flex: 50% 0 0;

            > .brokered-by-block {
                flex-basis: 100%;

                height: fit-content;

                > .provider-logo {
                    flex: ${Sizes.ICONS.L} 0 0;

                    max-width: ${Sizes.ICONS.XL};
                    max-height: ${Sizes.ICONS.XL};
                }
            }

            > .quote-summary-block {
                flex: 85% 1 0;

                .quote-summary-block-heading {
                    color: ${Colours.BLUEBERRY};
                }

                .summary-item-label {
                    color: ${Colours.BLACKBERRY};
                    min-width: 50px;
                    flex: 30% 3 1;
                }

                .summary-item-value {
                    max-width: 230px;
                    flex: 40% 1 0;
                }
            }
        }

        > .disclaimer {
            flex: 5% 0 0;

            border-top: 1px solid ${Colours.STONE};
        }
    }
`;

export default observer(ConfirmationPageTemplate);
